import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Förhandlingsframställning enligt hyresförhandlingslagen (1978:304) — v1.
 *
 * Hyresvärdens framställning om förhandling till en hyresgästorganisation
 * (t.ex. Hyresgästföreningen) enligt hyresförhandlingslagen. Ett ensidigt
 * dokument: hyresvärden begär förhandling, anger yrkande, grund och
 * underlag, och kallar (med stöd av 15 §) till första
 * förhandlingssammanträde. Endast hyresvärden undertecknar; motparten
 * (hyresgästorganisationen) är mottagare och svarar i förhandlingen.
 *
 * Eftersom bara hyresvärden skriver under och dokumentet sänds till
 * organisationen modelleras det som `signingMode: 'manual_delivery'`
 * (samma mönster som 18 e/18 d-meddelandena). Organisationen är en part
 * med rollen `other` (utanför SIGNING_ROLES), så den blockerar inte
 * signeringsrostern.
 *
 * Ported from `base-templates/Mall - förhandlingsframställning.docx`
 * (Haymanot-approved). Static legal text — DO NOT paraphrase or reorder.
 * Two Haymanot comments are carried into field help: nybyggnation i
 * befintligt hus avser endast nytillkomna ytor, och de föreslagna
 * sammanträdestiderna ska ligga inom tre veckor.
 */
import { Fragment } from 'react';
import { v, Checkbox, FieldRow, KeepTogether, PageBreak, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartySlot } from '../../../parties/index.js';
import { asDataTableRows, cellValue, formatCell, visibleColumns, } from '../../../data-table.js';
const SCOPE_OPTIONS = [
    {
        value: 'allman_utveckling',
        label: 'Ändring av hyran till följd av den ekonomiska utvecklingen på orten (årlig hyreshöjning)',
        code: 'general_development',
        summary: 'Negotiation concerns the annual rent change reflecting general local economic development.',
    },
    { value: 'andring_hyror', label: 'Ändring av hyror', code: 'rent_change', summary: 'Negotiation concerns a change of rents.' },
    { value: 'andra_villkor', label: 'Ändring av andra hyresvillkor', code: 'other_terms', summary: 'Negotiation concerns a change of other rental terms.' },
    {
        value: 'forsta_hyra_ombyggnad',
        label: 'Första hyra efter ombyggnad eller standardhöjande åtgärder',
        code: 'first_rent_renovation',
        summary: 'Negotiation concerns the first rent after renovation or standard-raising measures.',
    },
    { value: 'nybyggnation', label: 'Nybyggnation', code: 'new_construction', summary: 'Negotiation concerns rent for new construction.' },
    { value: 'annat', label: 'Annat', code: 'annat', summary: 'Negotiation concerns some other matter specified as free text.' },
];
// Förhandlingen avser — every alternative except "allmän ekonomisk
// utveckling på orten". Selecting one of these means the yrkande goes beyond
// the ordinary yearly adjustment, so a grund must be stated; a framställning
// that only covers the ekonomiska utvecklingen needs none.
const SCOPE_VALUES_REQUIRING_GROUNDS = SCOPE_OPTIONS.filter((o) => o.value !== 'allman_utveckling').map((o) => o.value);
// Grund för yrkandet. `label` is what the editor's multi_select shows (it
// keeps the explanatory parenthetical as guidance); `renderLabel`, when
// set, is what the document prints (the parenthetical is dropped there).
// `detailKey` is the per-ground freetext key rendered as a sub-line.
const GROUNDS_OPTIONS = [
    {
        value: 'investeringar',
        label: 'Genomförda investeringar',
        code: 'investments',
        summary: 'Grounds: investments that have been carried out.',
        detailKey: 'grounds_investeringar',
    },
    {
        value: 'bruksvarde',
        label: 'Förändrat bruksvärde',
        code: 'changed_utility_value',
        summary: 'Grounds: a changed bruksvärde (use value) of the dwelling.',
        detailKey: 'grounds_bruksvarde',
    },
    {
        value: 'jamforelse',
        label: 'Jämförelse med likvärdiga lägenheter i bruksvärdeshänseende (visar att hyresnivån för lägenheten är för låg)',
        code: 'comparison',
        summary: 'Grounds: comparison with equivalent dwellings showing the rent level is too low.',
        renderLabel: 'Jämförelse med likvärdiga lägenheter i bruksvärdeshänseende',
        detailKey: 'grounds_jamforelse',
    },
    { value: 'annat', label: 'Annat', code: 'annat', summary: 'Grounds: some other ground specified as free text.', detailKey: 'grounds_annat' },
];
const GROUNDS_VALUES = GROUNDS_OPTIONS.map((o) => o.value);
// Yrkande — "andra hyresvillkor". Selectable alternatives + an "Annat:"
// freetext (shown as checkbox lines in the document).
const ANDRA_VILLKOR_OPTIONS = [
    {
        value: 'individuell_matning',
        label: 'Individuell mätning och debitering av varm-/kallvatten eller el',
        code: 'individual_metering',
        summary: 'Term: individual metering and billing of hot/cold water or electricity.',
    },
    { value: 'andring_tillagg', label: 'Ändring av tillägg', code: 'change_surcharges', summary: 'Term: change of surcharges.' },
    { value: 'tillagg_per_lgh', label: 'Tillägg på hyran per lägenhet', code: 'surcharge_per_apartment', summary: 'Term: a rent surcharge per apartment.' },
    {
        value: 'gemensamhetslokaler',
        label: 'Ändrade villkor för nyttjande av gemensamhetslokaler eller andra utrymmen',
        code: 'common_areas',
        summary: 'Term: changed conditions for use of common areas or other spaces.',
    },
    { value: 'annat', label: 'Annat', code: 'annat', summary: 'Term: some other condition specified as free text.' },
];
// Förhandlingssammanträde — proposed times (min 2 per 15 §). Hand-entered;
// no Excel import controls.
const MEETING_PROPOSAL_COLUMNS = [
    { key: 'when', label: 'Datum och tid', width: '12rem' },
    { key: 'place', label: 'Plats', width: '12rem' },
];
const ENCLOSURE_OPTIONS = [
    { value: 'fastighetsbeskrivning', label: 'Fastighetsbeskrivning', code: 'property_description', summary: 'Enclosed: a property description.' },
    { value: 'lagenhetsforteckning', label: 'Lägenhetsförteckning', code: 'apartment_list', summary: 'Enclosed: an apartment register.' },
    { value: 'hyreslista', label: 'Hyreslista', code: 'rent_roll', summary: 'Enclosed: a rent roll.' },
    { value: 'ekonomiskt_underlag', label: 'Ekonomiskt underlag', code: 'financial_basis', summary: 'Enclosed: supporting financial documentation.' },
    { value: 'ombyggnadsbeskrivning', label: 'Ombyggnadsbeskrivning', code: 'renovation_description', summary: 'Enclosed: a renovation description.' },
];
/**
 * Hyreslista columns (from `base-templates/Mall för hyreslista[ med
 * servicetillägg].xlsx`). The servicetillägg columns + the computed "Årshyra
 * slutkund" only show for the "med servicetillägg"-varianten — which is
 * currently disabled (see the variant selector below). They stay gated on
 * `hyreslista_variant: 'service'`, a value that can no longer be set, so they
 * render dormant until the variant is re-enabled.
 */
const HYRESLISTA_COLUMNS = [
    { key: 'fastighetsagare', label: 'Fastighetsägare', width: '7rem' },
    { key: 'fastighet', label: 'Fastighet', width: '6rem' },
    { key: 'gatuadress', label: 'Gatuadress', width: '7rem' },
    { key: 'lagenhetsnummer', label: 'Lägenhetsnummer', width: '5rem' },
    { key: 'lantmateri_lgh_nr', label: 'Lantmäteriets lgh-nr', width: '5rem' },
    { key: 'lagenhetstyp', label: 'Lägenhetstyp', width: '5rem' },
    {
        key: 'yta',
        label: 'Lägenhets-yta',
        kind: 'number',
        width: '4rem',
        description: 'lägenhetsyta i kvadratmeter',
    },
    {
        key: 'arshyra',
        label: 'Årshyra',
        kind: 'currency',
        width: '5rem',
        description: 'ÅRSHYRA i kronor. Om indata är en MÅNADSHYRA: op=multiply, factor=12.',
    },
    {
        key: 'servicetillagg',
        label: 'Servicetillägg i årshyran',
        kind: 'currency',
        width: '5rem',
        visibleWhen: { hyreslista_variant: 'service' },
    },
    {
        key: 'arshyra_slutkund',
        label: 'Årshyra slutkund',
        kind: 'currency',
        width: '5rem',
        computed: { op: 'sum', inputs: ['arshyra', 'servicetillagg'] },
        visibleWhen: { hyreslista_variant: 'service' },
    },
];
export const metadata = {
    // Numbered document sections (per ADR-0049). Editor field-groups don't map
    // 1:1 to these (party/property fields are realised by their dedicated
    // panels), so `field.section` is a plain editor label and numbering +
    // anchors come from these refs + the Body ("document-only" migration).
    sections: [
        { ref: 'begaran', title: 'Begäran om förhandling' },
        { ref: 'forhandlingen-avser', title: 'Förhandlingen avser' },
        { ref: 'yrkande', title: 'Yrkande' },
        { ref: 'grund', title: 'Grund för yrkandet', visibleWhen: { grounds: GROUNDS_VALUES } },
        { ref: 'underlag', title: 'Underlag' },
        { ref: 'sammantrade', title: 'Första förhandlingssammanträde' },
        { ref: 'kontaktperson', title: 'Kontaktperson' },
        { ref: 'underskrift', title: 'Underskrift' },
    ],
    templateKey: 'forhandlingsframstallning',
    version: 'v1',
    category: 'Bostadshyressättningsavtal',
    title: 'Förhandlingsframställning enligt hyresförhandlingslagen (1978:304)',
    shortTitle: 'Förhandlingsframställning',
    formNumber: 'Formulär R-20',
    preamble: 'Hyresvärdens framställning om förhandling till hyresgästorganisationen enligt hyresförhandlingslagen (1978:304).',
    description: 'Hyresvärdens framställning om förhandling enligt hyresförhandlingslagen (1978:304), ställd till en hyresgästorganisation (t.ex. Hyresgästföreningen).\n\n' +
        'Används för att begära förhandling om t.ex. årlig hyreshöjning, ändrade hyror eller andra hyresvillkor, första hyra efter ombyggnad eller vid nybyggnation. Innehåller yrkande, grund, underlag och kallelse till första förhandlingssammanträde (15 § hyresförhandlingslagen).\n\n' +
        'Vem undertecknar: hyresvärden. Motparten (hyresgästorganisationen) är mottagare. Skrivs ut/signeras och skickas till organisationen.\n\n' +
        'Bilagor (fastighetsbeskrivning, lägenhetsförteckning, hyreslista m.m.) läggs till under fliken Bilagor — valfritt antal.',
    status: 'active',
    signingMode: 'manual_delivery',
    signingPlacementAnchors: 'per-party-label',
    recommendPersonalNumberInSigningBlock: true,
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'tenant_org',
            label: 'Hyresgästorganisation',
            role: 'other',
            ordinal: 0,
            required: true,
            // Mottagare — undertecknar inte framställningen.
            signs: false,
            help: 'Den hyresgästorganisation framställningen ställs till. Om din ' +
                'förhandlingspart är Hyresgästföreningen (802498-6465) ska ' +
                'framställan istället skickas till det regionkontor som förhandlar ' +
                'fastigheten.',
        },
    ],
    // Party projection (ADR-0065) — only the mottagare
    // (hyresgästorganisation) is captured; the hyresvärd identity is not a
    // party field on this framställan. The role name doubles as the label.
    partyProjection: [
        {
            slot: 'tenant_org',
            section: 'Mottagare',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'tenant_org_name',
                    label: 'Hyresgästorganisation',
                    requiredAtFinal: true,
                },
                { attr: 'address', key: 'tenant_org_address' },
                { attr: 'email', key: 'tenant_org_email' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'fastigheter',
            label: 'Fastigheter',
            multi: true,
            required: true,
            help: 'Lägg till alla fastigheter som framställningen avser.',
        },
    ],
};
export const variableSchema = [
    {
        key: 'request_date',
        label: 'Datum',
        type: 'date',
        section: 'Mottagare',
        requiredAtFinal: true,
        scopes: { copy: 'manual' },
    },
    // Mottagare — generated from metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'tenant_org'),
    // Fastigheter — one compact line per linked property (beteckning +
    // gatuadresser + postnummer/ort), aggregated server-side from the
    // linked properties (kind: 'property_list').
    {
        key: 'property_summary',
        label: 'Fastigheter',
        type: 'textarea',
        section: 'Fastighet',
        requiredAtFinal: true,
        autofill: { kind: 'property_list', field: 'summary' },
        help: 'En fastighet per rad. Hämtas automatiskt från länkade fastigheter.',
        scopes: { imported: true },
    },
    {
        key: 'scope_extent',
        label: 'Framställan avser',
        type: 'select',
        section: 'Fastighet',
        requiredAtFinal: true,
        default: 'hela',
        options: [
            { value: 'hela', label: 'Hela fastigheten', code: 'whole_property', summary: 'The request covers the entire property.' },
            { value: 'lagenheter', label: 'Följande lägenheter', code: 'specific_apartments', summary: 'The request covers only the listed apartments.' },
        ],
    },
    {
        key: 'scope_apartments',
        label: 'Lägenheter',
        type: 'textarea',
        section: 'Fastighet',
        visibleWhen: { scope_extent: 'lagenheter' },
        requiredWhen: { scope_extent: 'lagenheter' },
        help: 'Ange vilka lägenheter framställan avser.',
    },
    // Förhandlingen avser
    {
        key: 'negotiation_scope',
        label: 'Förhandlingen avser',
        type: 'multi_select',
        section: 'Förhandlingen avser',
        requiredAtFinal: true,
        options: SCOPE_OPTIONS.map((o) => ({ value: o.value, label: o.label, code: o.code, summary: o.summary })),
    },
    {
        key: 'negotiation_scope_annat',
        label: 'Annat (förhandlingen avser)',
        type: 'text',
        section: 'Förhandlingen avser',
        visibleWhen: { negotiation_scope: 'annat' },
        requiredWhen: { negotiation_scope: 'annat' },
    },
    // Yrkande
    {
        key: 'yrkande_vid_sammantrade',
        label: 'Hyresvärden presenterar sitt yrkande vid förhandlingssammanträdet',
        type: 'boolean',
        section: 'Yrkande',
        default: false,
        help: 'Om ikryssad anges inget skriftligt yrkande här — yrkandet presenteras ' +
            'vid sammanträdet. Fritextfälten nedan låses; endast ikraftträdandedatum fylls i.',
    },
    {
        key: 'yrkande_rent_change',
        label: 'Yrkande — ändring av hyror',
        type: 'textarea',
        section: 'Yrkande',
        visibleWhen: { negotiation_scope: ['allman_utveckling', 'andring_hyror'] },
        disabledWhen: { yrkande_vid_sammantrade: true },
        help: 'Att hyrorna ändras enligt följande (t.ex. "med 5 procent").',
    },
    {
        key: 'yrkande_andra_villkor_alts',
        label: 'Yrkande — andra hyresvillkor',
        type: 'multi_select',
        section: 'Yrkande',
        visibleWhen: { negotiation_scope: 'andra_villkor' },
        disabledWhen: { yrkande_vid_sammantrade: true },
        options: ANDRA_VILLKOR_OPTIONS.map((o) => ({ value: o.value, label: o.label, code: o.code, summary: o.summary })),
        help: 'Välj de villkor yrkandet avser. Använd "Annat" för övriga.',
    },
    {
        key: 'yrkande_andra_villkor_annat',
        label: 'Andra hyresvillkor — annat',
        type: 'textarea',
        section: 'Yrkande',
        visibleWhen: { negotiation_scope: 'andra_villkor', yrkande_andra_villkor_alts: 'annat' },
        disabledWhen: { yrkande_vid_sammantrade: true },
    },
    {
        key: 'yrkande_ombyggnad',
        label: 'Yrkande — första hyra efter ombyggnad',
        type: 'textarea',
        section: 'Yrkande',
        visibleWhen: { negotiation_scope: 'forsta_hyra_ombyggnad' },
        disabledWhen: { yrkande_vid_sammantrade: true },
        help: 'Att hyran bestäms till …',
    },
    {
        key: 'yrkande_nybyggnation',
        label: 'Yrkande — nybyggnation',
        type: 'textarea',
        section: 'Yrkande',
        visibleWhen: { negotiation_scope: 'nybyggnation' },
        disabledWhen: { yrkande_vid_sammantrade: true },
        help: 'Att hyran bestäms till …',
    },
    {
        key: 'nybyggnation_hyrestyp',
        label: 'Hyran ska vara',
        type: 'select',
        section: 'Yrkande',
        visibleWhen: { negotiation_scope: 'nybyggnation' },
        disabledWhen: { yrkande_vid_sammantrade: true },
        default: '',
        options: [
            { value: 'presumtionshyra', label: 'Presumtionshyra', code: 'presumption_rent', summary: 'Rent for new construction is set as presumtionshyra (presumption rent).' },
            { value: 'bruksvardeshyra', label: 'Bruksvärdeshyra', code: 'utility_value_rent', summary: 'Rent for new construction is set as bruksvärdeshyra (use-value rent).' },
        ],
        help: 'Nybyggnation i ett befintligt hus är endast möjlig om det rör sig om nybyggda ytor / ' +
            'lägenheter som tillkommit efter att ytor som tidigare användes för annat konverterats ' +
            'till bostäder.',
    },
    {
        key: 'yrkande_annat',
        label: 'Yrkande — annat',
        type: 'textarea',
        section: 'Yrkande',
        visibleWhen: { negotiation_scope: 'annat' },
        disabledWhen: { yrkande_vid_sammantrade: true },
        help: 'Fritt formulerat yrkande för det som valts under "Annat" i Förhandlingen avser.',
    },
    {
        key: 'effective_date',
        label: 'Föreslås träda i kraft från och med',
        type: 'date',
        section: 'Yrkande',
        requiredAtFinal: true,
        scopes: { copy: 'manual' },
    },
    // Grund för yrkandet
    {
        key: 'grounds',
        label: 'Grund för yrkandet',
        type: 'multi_select',
        section: 'Grund',
        options: GROUNDS_OPTIONS.map((o) => ({ value: o.value, label: o.label, code: o.code, summary: o.summary })),
        requiredWhen: { negotiation_scope: SCOPE_VALUES_REQUIRING_GROUNDS },
        help: 'Vid ändring av hyresvillkor utöver den ekonomiska utvecklingen på orten.',
    },
    {
        key: 'grounds_note',
        label: 'Grund behöver inte anges',
        type: 'note',
        section: 'Grund',
        visibleWhen: {
            negotiation_scope: 'allman_utveckling',
            not: { negotiation_scope: SCOPE_VALUES_REQUIRING_GROUNDS },
        },
        help: 'Förhandlingen avser endast ändring av hyran till följd av den ekonomiska ' +
            'utvecklingen på orten. Någon grund för yrkandet behöver då inte anges — ' +
            'lämnas rutorna tomma utelämnas hela avsnittet ur dokumentet.',
    },
    {
        key: 'grounds_investeringar',
        label: 'Genomförda investeringar',
        type: 'textarea',
        section: 'Grund',
        visibleWhen: { grounds: 'investeringar' },
    },
    {
        key: 'grounds_bruksvarde',
        label: 'Förändrat bruksvärde',
        type: 'textarea',
        section: 'Grund',
        visibleWhen: { grounds: 'bruksvarde' },
    },
    {
        key: 'grounds_jamforelse',
        label: 'Jämförelse med likvärdiga lägenheter i bruksvärdeshänseende',
        type: 'textarea',
        section: 'Grund',
        visibleWhen: { grounds: 'jamforelse' },
    },
    {
        key: 'grounds_annat',
        label: 'Annat (grund)',
        type: 'textarea',
        section: 'Grund',
        visibleWhen: { grounds: 'annat' },
    },
    // Underlag
    {
        key: 'enclosures',
        label: 'Följande handlingar bifogas',
        type: 'multi_select',
        section: 'Underlag',
        options: ENCLOSURE_OPTIONS.map((o) => ({ value: o.value, label: o.label, code: o.code, summary: o.summary })),
        help: 'Ladda upp handlingarna under fliken Bilagor (valfritt antal).',
    },
    // Första förhandlingssammanträde — list of proposed times (min 2)
    {
        key: 'meeting_proposals',
        label: 'Förslag på tid och plats',
        type: 'data_table',
        section: 'Förhandlingssammanträde',
        columns: MEETING_PROPOSAL_COLUMNS,
        minRows: 2,
        xlsxImport: false,
        requiredAtFinal: true,
        help: 'Minst två förslag, samtliga inom tre veckor (15 § hyresförhandlingslagen).',
    },
    {
        key: 'meeting_digital',
        label: 'Digitalt möte godtas via',
        type: 'text',
        section: 'Förhandlingssammanträde',
        help: 'T.ex. länk eller tjänst. Lämnas tomt om digitalt möte inte erbjuds.',
    },
    {
        key: 'alt_proposal_deadline',
        label: 'Senast för förslag på alternativ tid',
        type: 'date',
        section: 'Förhandlingssammanträde',
        help: 'Om föreslagen tid eller plats inte kan accepteras ska motparten föreslå alternativ tidpunkt senast detta datum. Bör ligga inom tre veckor.',
        scopes: { copy: 'manual' },
    },
    // Kontaktperson
    {
        key: 'contact_name',
        label: 'Namn',
        type: 'text',
        section: 'Kontaktperson',
        requiredAtFinal: true,
    },
    {
        key: 'contact_phone',
        label: 'Telefon',
        type: 'text',
        section: 'Kontaktperson',
    },
    {
        key: 'contact_email',
        label: 'E-post',
        type: 'text',
        section: 'Kontaktperson',
    },
    // Bilaga – Hyreslista (visas när "Hyreslista" valts under Underlag).
    // Renderas sist i dokumentet, efter underskriften, på en ny sida.
    {
        key: 'hyreslista_hint',
        label: 'Hyreslista ej tillagd',
        type: 'note',
        section: 'Hyreslista',
        visibleWhen: { not: { enclosures: 'hyreslista' } },
        help: 'Bocka i "Hyreslista" under Underlag ovan för att lägga till den som bilaga och fylla i den här.',
    },
    // Hyreslista-varianten "med servicetillägg" är avstängd: hyreslistan har
    // bara en variant (utan servicetillägg), så variantväljaren visas inte.
    // För att återaktivera: lägg tillbaka select-fältet nedan med båda
    // alternativen. Servicekolumnerna finns kvar i HYRESLISTA_COLUMNS, gärdade
    // på `hyreslista_variant: 'service'`, och tänds igen så snart varianten kan
    // väljas.
    {
        key: 'hyreslista_rows',
        label: 'Hyreslista',
        type: 'data_table',
        section: 'Hyreslista',
        visibleWhen: { enclosures: 'hyreslista' },
        columns: HYRESLISTA_COLUMNS,
        help: 'En rad per lägenhet. "Årshyra slutkund" räknas ut automatiskt. Bifogas sist i dokumentet.',
    },
];
export const sampleValues = {
    request_date: '2026-06-02',
    tenant_org_name: 'Hyresgästföreningen Region Stockholm',
    tenant_org_address: 'Arenavägen 55\n121 77 Johanneshov',
    tenant_org_email: 'stockholm@hyresgastforeningen.se',
    property_summary: 'Klockaren 5, Götgatan 35, 116 21 Stockholm\nKlockaren 6, Götgatan 37, 116 21 Stockholm',
    scope_extent: 'hela',
    scope_apartments: '',
    negotiation_scope: ['allman_utveckling', 'andra_villkor'],
    negotiation_scope_annat: '',
    yrkande_vid_sammantrade: false,
    yrkande_rent_change: 'Att hyrorna ändras med 4,5 procent.',
    yrkande_andra_villkor_alts: ['individuell_matning', 'tillagg_per_lgh'],
    yrkande_andra_villkor_annat: '',
    yrkande_ombyggnad: '',
    yrkande_nybyggnation: '',
    nybyggnation_hyrestyp: '',
    yrkande_annat: '',
    effective_date: '2027-01-01',
    grounds: ['investeringar', 'bruksvarde'],
    grounds_investeringar: 'Stambyte och nya badrum genomförda 2025.',
    grounds_bruksvarde: 'Höjd standard genom nya kök och förbättrad ventilation.',
    grounds_jamforelse: '',
    grounds_annat: '',
    enclosures: ['fastighetsbeskrivning', 'hyreslista', 'ekonomiskt_underlag'],
    meeting_proposals: [
        { when: '2026-06-18 kl. 13:00', place: 'Götgatan 35, konferensrummet' },
        { when: '2026-06-23 kl. 10:00', place: 'Digitalt möte' },
    ],
    meeting_digital: 'Microsoft Teams (länk skickas separat)',
    alt_proposal_deadline: '2026-06-12',
    contact_name: 'Erik Hyresförvaltare',
    contact_phone: '08-123 45 67',
    contact_email: 'forhandling@bostadsagarna.se',
    hyreslista_variant: 'plain',
    hyreslista_rows: [
        {
            fastighetsagare: 'Bostadsägarna Stockholm AB',
            fastighet: 'Klockaren 5',
            gatuadress: 'Götgatan 35',
            lagenhetsnummer: '1101',
            lantmateri_lgh_nr: '1101',
            lagenhetstyp: '2 rok',
            yta: 58,
            arshyra: 102000,
            servicetillagg: 6000,
        },
        {
            fastighetsagare: 'Bostadsägarna Stockholm AB',
            fastighet: 'Klockaren 5',
            gatuadress: 'Götgatan 35',
            lagenhetsnummer: '1102',
            lantmateri_lgh_nr: '1102',
            lagenhetstyp: '3 rok',
            yta: 74,
            arshyra: 126000,
            servicetillagg: 7200,
        },
    ],
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
const arr = (val) => (Array.isArray(val) ? val : []);
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
/** A checkbox line whose checked state comes from a multi_select array. */
function OptionLine({ checked, children, }) {
    return (_jsxs("p", { className: "mt-1", children: [_jsx(Checkbox, { checked: checked }), children] }));
}
/** The hyreslista rent-roll table (the appendix rendered after signing). */
function HyreslistaTable({ values, }) {
    const cols = visibleColumns(HYRESLISTA_COLUMNS, values);
    const rows = asDataTableRows(values.hyreslista_rows);
    return (_jsx("div", { className: "max-w-full overflow-x-auto", children: _jsxs("table", { className: "w-full min-w-max border-collapse text-[9px]", children: [_jsx("thead", { children: _jsx("tr", { children: cols.map((c) => (_jsx("th", { className: "border border-stone-400 bg-stone-100 px-1 py-0.5 text-left font-semibold", children: c.label }, c.key))) }) }), _jsx("tbody", { children: rows.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: cols.length, className: "border border-stone-400 px-1 py-2 text-center italic text-stone-400", children: "(Inga rader ifyllda \u00E4nnu)" }) })) : (rows.map((row, i) => (_jsx("tr", { children: cols.map((c) => {
                            const numeric = c.kind === 'currency' || c.kind === 'number';
                            return (_jsx("td", { className: 'border border-stone-400 px-1 py-0.5 ' +
                                    (numeric ? 'text-right font-mono tabular-nums' : ''), children: formatCell(c, cellValue(c, row)) }, c.key));
                        }) }, i)))) })] }) }));
}
export function Body({ values, mode, signatories, }) {
    const scope = arr(values.negotiation_scope);
    const grounds = arr(values.grounds);
    const enclosures = arr(values.enclosures);
    const andraVillkorAlts = arr(values.yrkande_andra_villkor_alts);
    const has = (k) => scope.includes(k);
    // When the värd opts to present the yrkande verbally at the meeting, the
    // specific written yrkanden are replaced by a single statement (the
    // ikraftträdandedatum is still shown). Not `cond`-gated: in blank mode the
    // box is unticked, so the printable form still lists every yrkande-val.
    const presenterar = values.yrkande_vid_sammantrade === true;
    const showRentYrkande = cond(mode, has('allman_utveckling') || has('andring_hyror'));
    const showAndraVillkor = cond(mode, has('andra_villkor'));
    const showOmbyggnad = cond(mode, has('forsta_hyra_ombyggnad'));
    const showNybyggnation = cond(mode, has('nybyggnation'));
    const showYrkandeAnnat = cond(mode, has('annat'));
    const showApartments = cond(mode, values.scope_extent === 'lagenheter');
    // Mötesförslag — show the filled list; on a blank printable form seed two
    // empty rows so there's something to write on.
    const proposals = asDataTableRows(values.meeting_proposals);
    const displayProposals = proposals.length > 0 ? proposals : mode === 'blank' ? [{}, {}] : [];
    return (_jsxs(_Fragment, { children: [_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-1 text-center text-[13px] font-semibold uppercase tracking-wide", children: "F\u00F6rhandlingsframst\u00E4llning" }), _jsx("p", { className: "mb-4 text-center text-[10.5px] italic text-stone-600", children: "enligt hyresf\u00F6rhandlingslagen (1978:304)" }), _jsx(FieldRow, { label: "Datum", children: v(values.request_date) }), _jsxs("div", { className: "mt-3 border border-stone-300", children: [_jsx("p", { className: "bg-stone-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-stone-600", children: "Till" }), _jsx(FieldRow, { label: "Hyresg\u00E4storganisation", children: v(values.tenant_org_name) }), _jsx(FieldRow, { label: "Adress", children: v(values.tenant_org_address) }), _jsx(FieldRow, { label: "E-post", children: v(values.tenant_org_email) })] }), _jsxs(Section, { sectionRef: "begaran", title: "Beg\u00E4ran om f\u00F6rhandling", children: [_jsx("p", { children: "Hyresv\u00E4rden beg\u00E4r h\u00E4rmed f\u00F6rhandling enligt hyresf\u00F6rhandlingslagen betr\u00E4ffande f\u00F6ljande fastighet(er):" }), _jsx("div", { className: "mt-2 border border-stone-300", children: _jsxs("div", { className: "grid gap-2 py-1", style: { gridTemplateColumns: '18% 82%' }, children: [_jsx("span", { className: "min-w-0 font-sans text-[10px] text-stone-600", style: { overflowWrap: 'anywhere' }, children: "Fastigheter" }), _jsx("span", { className: "min-w-0 whitespace-pre-line", style: { overflowWrap: 'anywhere' }, children: v(values.property_summary) })] }) }), _jsxs("div", { className: "mt-2", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wide text-stone-600", children: "Framst\u00E4llan avser" }), _jsx(OptionLine, { checked: cond(mode, values.scope_extent !== 'lagenheter'), children: "Hela fastigheten" }), _jsxs(OptionLine, { checked: cond(mode, values.scope_extent === 'lagenheter'), children: ["F\u00F6ljande l\u00E4genheter:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: showApartments ? v(values.scope_apartments) : '' })] })] })] }), _jsx(Section, { sectionRef: "forhandlingen-avser", title: "F\u00F6rhandlingen avser", children: SCOPE_OPTIONS.map((o) => (_jsx(OptionLine, { checked: cond(mode, has(o.value)), children: o.value === 'annat' ? (_jsxs(_Fragment, { children: ["Annat:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: cond(mode, has('annat')) ? v(values.negotiation_scope_annat) : '' })] })) : (o.label) }, o.value))) }), _jsxs(Section, { sectionRef: "yrkande", title: "Yrkande", children: [_jsx("p", { className: "font-semibold", children: "Hyresv\u00E4rden yrkar:" }), presenterar ? (_jsx("p", { className: "mt-2", children: "Hyresv\u00E4rden presenterar sitt yrkande vid f\u00F6rhandlingssammantr\u00E4det." })) : (_jsxs(_Fragment, { children: [showRentYrkande && (_jsx("p", { className: "mt-2 whitespace-pre-line", children: v(values.yrkande_rent_change) })), showAndraVillkor && (_jsx("div", { className: "mt-2", children: ANDRA_VILLKOR_OPTIONS.map((o) => (_jsx(OptionLine, { checked: cond(mode, andraVillkorAlts.includes(o.value)), children: o.value === 'annat' ? (_jsxs(_Fragment, { children: ["Annat:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: cond(mode, andraVillkorAlts.includes('annat'))
                                                            ? v(values.yrkande_andra_villkor_annat)
                                                            : '' })] })) : (o.label) }, o.value))) })), showOmbyggnad && (_jsxs("p", { className: "mt-2 whitespace-pre-line", children: ["F\u00F6rsta hyra efter ombyggnad/standardh\u00F6jande \u00E5tg\u00E4rder:", ' ', v(values.yrkande_ombyggnad)] })), showNybyggnation && (_jsxs("p", { className: "mt-2 whitespace-pre-line", children: ["Nybyggnation: ", v(values.yrkande_nybyggnation), cond(mode, !!values.nybyggnation_hyrestyp) && (_jsxs(_Fragment, { children: [' ', "Hyran ska vara", ' ', _jsx("span", { className: "font-medium", children: values.nybyggnation_hyrestyp === 'bruksvardeshyra'
                                                            ? 'bruksvärdeshyra'
                                                            : values.nybyggnation_hyrestyp === 'presumtionshyra'
                                                                ? 'presumtionshyra'
                                                                : '____________' }), "."] }))] })), showYrkandeAnnat && (_jsx("p", { className: "mt-2 whitespace-pre-line", children: v(values.yrkande_annat) }))] })), _jsxs("p", { className: "mt-3", children: ["Den beg\u00E4rda \u00E4ndringen f\u00F6resl\u00E5s tr\u00E4da i kraft fr\u00E5n och med:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.effective_date) })] })] }), _jsxs(Section, { sectionRef: "grund", title: "Grund f\u00F6r yrkandet", children: [_jsx("p", { children: "Som grund f\u00F6r yrkandet \u00E5beropas:" }), GROUNDS_OPTIONS.map((o) => {
                                // Document prints `renderLabel` when set (parenthetical guidance
                                // is editor-only). Per-ground freetext is rendered only when the
                                // ground is checked AND text was entered — no empty rad annars.
                                const renderLabel = o.renderLabel ?? o.label;
                                const detail = values[o.detailKey];
                                const hasDetail = typeof detail === 'string' && detail.trim() !== '';
                                return (_jsxs("div", { children: [_jsx(OptionLine, { checked: cond(mode, grounds.includes(o.value)), children: renderLabel }), cond(mode, grounds.includes(o.value)) && hasDetail && (_jsx("p", { className: "ml-5 whitespace-pre-line text-stone-700", children: v(detail) }))] }, o.value));
                            })] }), _jsxs(Section, { sectionRef: "underlag", title: "Underlag", children: [_jsx("p", { children: "F\u00F6ljande handlingar bifogas:" }), ENCLOSURE_OPTIONS.map((o) => (_jsx(OptionLine, { checked: cond(mode, enclosures.includes(o.value)), children: o.label }, o.value))), _jsx("p", { className: "mt-1 text-[10px] italic text-stone-500", children: "Eventuella ytterligare bilagor bifogas separat." })] }), _jsxs(Section, { sectionRef: "sammantrade", title: "F\u00F6rsta f\u00F6rhandlingssammantr\u00E4de", children: [_jsx("p", { children: "Med st\u00F6d av 15 \u00A7 hyresf\u00F6rhandlingslagen kallas h\u00E4rmed till f\u00F6rsta f\u00F6rhandlingssammantr\u00E4de. Minst tv\u00E5 f\u00F6rslag, samtliga inom tre veckor:" }), _jsx("div", { className: "mt-2 border border-stone-300", children: displayProposals.map((p, i) => (_jsxs(Fragment, { children: [_jsx(FieldRow, { label: `Förslag ${i + 1} — datum och tid`, children: v(p.when) }), _jsx(FieldRow, { label: `Förslag ${i + 1} — plats`, children: v(p.place) })] }, i))) }), cond(mode, !!values.meeting_digital) && (_jsxs("p", { className: "mt-2", children: ["Hyresv\u00E4rden godtar digitalt m\u00F6te som alternativ via:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.meeting_digital) })] })), _jsxs("p", { className: "mt-2", children: ["Om f\u00F6reslagen tid eller plats inte kan accepteras ombeds motparten snarast, dock senast", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.alt_proposal_deadline) }), ", f\u00F6resl\u00E5 alternativ tidpunkt."] })] }), _jsx(Section, { sectionRef: "kontaktperson", title: "Kontaktperson", children: _jsxs("div", { className: "border border-stone-300", children: [_jsx(FieldRow, { label: "Namn", children: v(values.contact_name) }), _jsx(FieldRow, { label: "Telefon", children: v(values.contact_phone) }), _jsx(FieldRow, { label: "E-post", children: v(values.contact_email) })] }) }), _jsx(KeepTogether, { children: _jsx(Section, { sectionRef: "underskrift", title: "Underskrift", children: _jsx(SigningBlock, { parties: [{ label: 'Hyresvärd', role: 'lessor', ordinal: 0 }], signatories: signatories }) }) })] }), cond(mode, enclosures.includes('hyreslista')) && (_jsxs(_Fragment, { children: [_jsx(PageBreak, {}), _jsx("p", { className: "mb-3 text-[11px] font-bold uppercase tracking-wide", children: "Bilaga \u2013 Hyreslista" }), _jsx(HyreslistaTable, { values: values })] }))] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map